import { IntakeCaseState, IntakeCategory, IntakeQueueItem } from '../services/inboundInboxService';
import { WhatsAppQueueItem } from '../services/whatsappService';
import { formatStandardDate, formatStandardDateTime, formatStandardTime, formatGmailDate } from './dateTime';
import { getCategoryDisplay, getReplySourceLabel, getVerificationLabel } from './inboundLabels';

export const badgeClass = (category: IntakeCategory) => {
  if (category === 'PRIVATE_BALLOON' || category === 'CHARTER') return 'bg-[#fef7e0] text-[#b06000]';
  if (category === 'SHARED_BALLOON' || category === 'SCHEDULED') return 'bg-[#e8f0fe] text-[#1a73e8]';
  if (category === 'CONCIERGE_VIP' || category === 'CONCIERGE') return 'bg-[#f3e8fd] text-[#7b1fa2]';
  if (category === 'BUSH_LUNCH') return 'bg-[#f0f4c3] text-[#558b2f]';
  if (category === 'TRANSFERS') return 'bg-[#e0f2f1] text-[#00695c]';
  if (category === 'LODGE_CAMP') return 'bg-[#e6f4ea] text-[#137333]';
  if (category === 'PAYMENTS') return 'bg-[#fff8e1] text-[#f57f17]';
  if (category === 'OPERATIONS') return 'bg-[#f1f3f4] text-[#3c4043]';
  if (category === 'GENERAL_INQUIRY' || category === 'NON_FLIGHT') return 'bg-[#f1f3f4] text-[#5f6368]';
  if (category === 'NEEDS_REVIEW' || category === 'UNCERTAIN') return 'bg-[#fef7e0] text-[#b06000]';
  return 'bg-[#fce8e6] text-[#c5221f]';
};

export const slaClass = (level?: string | null) => {
  if (level === 'RED') return 'bg-[#fce8e6] text-[#c5221f]';
  if (level === 'AMBER') return 'bg-[#fef7e0] text-[#b06000]';
  return 'bg-[#e6f4ea] text-[#137333]';
};

export const verificationClass = (status?: string | null) => {
  const key = String(status || '').toUpperCase();
  if (key === 'VERIFIED') return 'bg-[#e6f4ea] text-[#137333]';
  if (key === 'TRUSTED') return 'bg-[#e8f0fe] text-[#1a73e8]';
  return 'bg-[#fef7e0] text-[#b06000]';
};

export const replyStatusClass = (source?: string | null) => {
  const label = getReplySourceLabel(source).toUpperCase();
  if (label === 'WEB INBOX') return 'bg-[#e0f2f1] text-[#00695c]';
  if (label === 'INBOUND HUB') return 'bg-[#e6f4ea] text-[#137333]';
  return 'bg-[#ede7f6] text-[#5e35b1]';
};

export const caseStateClass = (state?: IntakeCaseState | null) => {
  if (state === 'RESOLVED_WON') return 'bg-[#e6f4ea] text-[#137333]';
  if (state === 'RESOLVED_LOST') return 'bg-[#fce8e6] text-[#c5221f]';
  if (state === 'WAITING_CLIENT') return 'bg-[#ede7f6] text-[#5e35b1]';
  if (state === 'NO_ACTION') return 'bg-[#f1f3f4] text-[#5f6368]';
  return 'bg-[#fef7e0] text-[#b06000]';
};

export const isOperationalInbound = (item?: { category?: string | null; flightRelated?: boolean | null }) => {
  const category = String(item?.category || '').toUpperCase();
  if (category === 'NON_FLIGHT' || category === 'GENERAL_INQUIRY' || category === 'SPAM') return false;
  return !!item?.flightRelated;
};

export const stripQuotedContent = (value?: string | null) => {
  const source = String(value || '').replace(/\r/g, '').trim();
  if (!source) return '';
  const lines = source.split('\n');
  const cleaned: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (/^>/.test(trimmed)) continue;
    if (/^on .+ wrote:$/i.test(trimmed)) break;
    if (/^on .+ at .+, .+ wrote:$/i.test(trimmed)) break;
    if (/^-{2,}\s*original message\s*-{2,}$/i.test(trimmed)) break;
    if (/^-{2,}\s*forwarded message\s*-{2,}$/i.test(trimmed)) break;
    if (/^from:\s/i.test(trimmed) && cleaned.length > 2) break;
    if (/^sent:\s/i.test(trimmed) && cleaned.length > 2) break;
    if (/^subject:\s/i.test(trimmed) && cleaned.length > 2) break;
    cleaned.push(line);
  }
  return cleaned.join('\n').trim();
};

export const decodeHtmlEntities = (value?: string | null): string => {
  const input = String(value || '');
  if (!input) return '';
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const el = document.createElement('textarea');
    el.innerHTML = input;
    return String(el.value || '');
  }
  return input
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
};

export const htmlToPlain = (value?: string | null) =>
  decodeHtmlEntities(
    String(value || '')
    .replace(/<blockquote[\s\S]*?<\/blockquote>/gi, ' ')
    .replace(/<div[^>]*class=["'][^"']*(gmail_quote|yahoo_quoted)[^"']*["'][^>]*>[\s\S]*?<\/div>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim()
  );

export const bodyPreview = (text?: string | null, html?: string | null, maxLen = 340) => {
  const plain = stripQuotedContent(decodeHtmlEntities(text)) || stripQuotedContent(htmlToPlain(html));
  if (!plain) return '(No body content)';
  if (plain.length <= maxLen) return plain;
  return `${plain.slice(0, maxLen).trim()}...`;
};

export const timelineAt = (msg: { receivedAt?: string; sentAt?: string; createdAt?: string }) => {
  const raw = msg.receivedAt || msg.sentAt || msg.createdAt || '';
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

export const formatDateShort = (value?: string | null) => {
  return formatGmailDate(value);
};

export const formatTimeShort = (value?: string | null) => {
  return formatStandardTime(value);
};

export const compactDateTime = (value?: string | null) => {
  return formatGmailDate(value);
};

export const minutesSince = (value?: string | null): number | null => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.round((Date.now() - ms) / 60000));
};

export const hasFreshInboundActivity = (item: WhatsAppQueueItem): boolean => {
  const inboundMs = new Date(item.lastInboundAt || '').getTime();
  if (!Number.isFinite(inboundMs)) return false;
  const outboundMs = new Date(item.lastOutboundAt || '').getTime();
  const inboundIsLatest = !Number.isFinite(outboundMs) || inboundMs > outboundMs;
  return inboundIsLatest && Date.now() - inboundMs <= 30 * 60 * 1000;
};

export const whatsappStatusLabel = (status?: string | null) => {
  if (status === 'WAITING_CLIENT') return 'Waiting Client';
  if (status === 'RESOLVED_WON') return 'Resolved Won';
  if (status === 'RESOLVED_LOST') return 'Resolved Lost';
  return 'Open';
};

export const whatsappFilterLabel = (filter: 'OVERDUE' | 'NEEDS_REPLY' | 'RESOLVED_WON' | 'RESOLVED_LOST' | 'ALL') => {
  if (filter === 'NEEDS_REPLY') return 'Needs Reply';
  if (filter === 'RESOLVED_WON') return 'Resolved Won';
  if (filter === 'RESOLVED_LOST') return 'Resolved Lost';
  return filter === 'ALL' ? 'All' : 'Overdue';
};

export const whatsappMessagePreview = (message?: { body?: string | null; messageType?: string | null } | null) => {
  const body = String(message?.body || '').trim();
  if (body) return body;
  const type = String(message?.messageType || '').toUpperCase();
  if (type === 'IMAGE') return '[Image]';
  if (type === 'VIDEO') return '[Video]';
  if (type === 'AUDIO') return '[Audio]';
  if (type === 'DOCUMENT') return '[Document]';
  if (type === 'INTERACTIVE') return '[Interactive]';
  if (type === 'TEMPLATE') return '[Template]';
  if (type === 'SYSTEM') return '[System event]';
  return '[Unsupported message]';
};

export const getLastAction = (item: IntakeQueueItem): { label: string; at: string; by: string } | null => {
  const actions: Array<{ label: string; at: string; by: string }> = [];
  if (item.assignedAt) {
    actions.push({
      label: 'Assigned',
      at: item.assignedAt,
      by: item.assignedByName || item.assignedToName || item.assignedToEmail || '—',
    });
  }
  if (item.convertedAt) {
    actions.push({
      label: 'Converted',
      at: item.convertedAt,
      by: item.convertedByName || item.convertedByEmail || '—',
    });
  }
  if (!actions.length) return null;
  actions.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  return actions[0];
};

export const inboundCategoryTone = (item: {
  category?: string | null;
  linkedQuoteId?: string | null;
  linkedQuoteMode?: string | null;
  convertedAt?: string | null;
}) => getCategoryDisplay(item);

export const inboundVerificationLabel = getVerificationLabel;
